import CardCommon from '@op/components/CardCommon';
import React, { useMemo, useState } from 'react';
import { Col, Row } from 'tea-component';
import { lineIcon, vsIcon } from '@op/constants/chart';
import KeyMetricsItem from '../component/KeyMetricsItem';
import './index.scss';
import GoogleTrendsCharts from '../../CompareKeyMetrics/GoogleTrends/GoogleTrendsCharts';
import { formatDateFunc } from '@op/utils/time';
import ProgressColumn from '../../CompareKeyMetrics/component/ProgressColumn';
import { scoreFormat } from '@op/utils/value';
import { DBO_TM } from '@op/utils/format';
import useLanguage from '@op/hooks/useLanguage';
import { useGetRankList } from '../hooks/rank';
import { usePRTimeline } from './hooks/usePRTimeline';
import { RankKeyMeticsItem } from '../component/TrendCardTips';
import { useHistory } from 'umi';
import { goToCompetitorKeyMetrics } from '@op/utils/url';
import {
  useDrilldownParams,
  EDrilldownMode,
} from '@op/hooks/useDrilldownParams';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import BubbleCommon from '@op/components/BubbleCommon';
import { keyMetricsPRTips } from '@op/components/BubbleCommon/copywritingKey/keyMetrics';
import { TableHeight } from '../../CompareKeyMetrics/constants';

const keyMetricsOptions: Array<any> = [
  {
    text: 'Total Articles',
    key: 'all_articles',
  },
  {
    text: 'Negative Articles',
    key: 'negative_articles',
    otherParams: { sentiment: [1] },
  },
];
export default function PR({
  filter,
  wholeGameInfo,
  gameInfo,
  games,
  gameData,
  showRank,
}: {
  filter: any;
  wholeGameInfo: any;
  gameInfo: any;
  games: Array<any>;
  gameData: any;
  showRank: boolean;
}) {
  return (
    <CardCommon
      title={<span style={{ fontSize: 20 }}>PR</span>}
      logBtnId="PR"
      contentCard
    >
      <CardCommon className="card-common-dbo-key-metrics">
        <Row className="dbo-pr-news-trends">
          <Col span={6}>
            <NewsKeyMetrics
              filter={filter}
              games={games}
              gameData={gameData}
              showRank={showRank}
            ></NewsKeyMetrics>
          </Col>
          <Col span={18}>
            <GoogleTrends
              filter={filter}
              games={games}
              gameData={gameData}
              showRank={showRank}
              wholeGameInfo={wholeGameInfo}
            ></GoogleTrends>
          </Col>
        </Row>
      </CardCommon>
    </CardCommon>
  );
}

function GoogleTrends({
  filter,
  gameData,
  games,
  showRank,
  wholeGameInfo,
}: {
  filter: any;
  games: Array<any>;
  gameData: any;
  showRank: boolean;
  wholeGameInfo: any;
}) {
  const { download, downloading } = useDownloadParams();
  const commonParams = useMemo(() => {
    return {
      ...filter,
      stats_types: ['google_trends'],
      tab_type: 'google_trends',
    };
  }, [filter]);

  const { timelineParams, rankParams } = useMemo(() => {
    return {
      timelineParams: {
        ...commonParams,
        games: [],
      },
      rankParams: {
        ...commonParams,
        games,
      },
    };
  }, [commonParams]);

  const [chartType, setChartType] = useState('line');
  const { formatMessage } = useLanguage();
  const columns: any = useMemo(
    () => [
      {
        header: formatMessage({ id: 'Date' }),
        key: 'time',
        align: 'left',
        render: (cvm: any) => formatDateFunc(filter?.date_type)(cvm.time),
      },
      {
        header: formatMessage({ id: 'Google Trends' }),
        key: 'value',
        align: 'center',
        render: (cvm: any) => (
          <ProgressColumn
            percentage={cvm.ratio}
            emptyValue={'-'}
            value={cvm.value}
            formateValue={(value) => scoreFormat(value, 0)}
            oldTime={[]}
            newTime={[]}
            oldValue={''}
          ></ProgressColumn>
        ),
      },
    ],
    [formatMessage, filter?.date_type],
  );

  const { loading, getRankList } = useGetRankList({
    filter: rankParams,
    gameData,
  });

  const history = useHistory();
  function changeChartType(type: any) {
    if (type === 'vs') {
      goToCompetitorKeyMetrics(history, filter, 'Google Trends');
    } else {
      setChartType(type || 'line');
    }
  }

  const { Competitor_Rank_Hide } = useGetSingePageSourceRule({
    keys: ['Competitor_Rank_Hide'],
  });
  function getOperationsList() {
    const list = [lineIcon];

    if (!Competitor_Rank_Hide) {
      list.unshift(vsIcon);
    }

    return list;
  }

  return (
    <div className="dbo-pr-trends">
      <CardCommon
        title={
          <div className="dbo-pr-trends-hd">
            <div className="dbo-pr-trends-title">
              {formatMessage({ id: 'Google Trends' })}
            </div>
            <div className="dbo-compare-operation">
              <RankKeyMeticsItem
                getRankList={getRankList}
                valueKey={'total'}
                valueFormat={(value) => scoreFormat(value, 0)}
                noShow={showRank}
                title={formatMessage({ id: 'Google Trends' })}
              ></RankKeyMeticsItem>
            </div>
          </div>
        }
        hasDownload
        downloadLoading={downloading}
        contentCard
        operationInfo={[chartType, changeChartType]}
        operations={getOperationsList()}
        defaultOperation="line"
        logBtnId="KMPR"
        handleDownloadOnClick={() => {
          download({
            key: 'KeyMetricsGoogleTrends',
            filter: {
              ...filter,
              games: [],
              stats_types: ['google_trends'],
              tab_type: 'google_trends',
            },
            fileName: 'Key Metrics Google Trends',
          });
        }}
      >
        <GoogleTrendsCharts
          filter={timelineParams}
          gameData={gameData}
          chartType={chartType}
          sortColumns={[]}
          tableColumns={columns}
        />
      </CardCommon>
    </div>
  );
}

function NewsKeyMetrics({
  filter,
  games,
  gameData,
  showRank,
}: {
  filter: any;
  games: Array<any>;
  gameData: any;
  showRank: boolean;
}) {
  const { formatMessage } = useLanguage();
  const isToday = useMemo(() => DBO_TM.isToday(filter), [filter]);
  const params = useMemo(() => {
    return {
      ...filter,
      games: games,
      stats_types: ['pr'],
      by_type: 'pr',
      tab_type: 'pr',
      is_today: isToday,
    };
  }, [filter, isToday]);

  const { data, loading, getRankList } = useGetRankList({
    filter: params,
    gameData,
  });

  // Fetch timeline once after stats_list (rank) data is available
  const { statisticsMap } = usePRTimeline({
    filter: params,
    valueMap: data,
    keys: keyMetricsOptions.map(item => item.key),
  });

  const { goPage } = useDrilldownParams();

  return (
    <div className="dbo-pr-news">
      <CardCommon
        title={formatMessage({ id: 'News' })}
        hasTitleArrow
        handleTitleOnClick={() => {
          goPage({
            path: `/opinion/News`,
            filter,
            // News 为特殊页面，走本地缓存
            mode: EDrilldownMode.Local,
          });
        }}
        contentCard
        dropdownsFilterBeforeElement={(
          <>
            {
              isToday && data?.newTime && !loading && (
                <BubbleCommon
                  tipsMap={keyMetricsPRTips(DBO_TM.timeTitle({
                    start_time: data.newTime[0],
                    end_time: data.newTime[1],
                    date_type: filter?.date_type || 'daily',
                  }))}
                  childClassName='dbo-cardcommon-subtitle'
                >
                  <span className="dbo-pr-news-tips">
                    {DBO_TM.timeTitle({
                      start_time: data.newTime[0],
                      end_time: data.newTime[1],
                      date_type: filter?.date_type || 'daily',
                    })}
                  </span>
                </BubbleCommon>
              )
            }
          </>
        )}
        loading={loading}
        loadingHeight={TableHeight.CHART_WITHOUT_SUBTITLE}
      >
        <>
          {keyMetricsOptions.map((item: any) => (
            <KeyMetricsItem
              valueMap={data}
              key={item.text}
              item={item}
              filter={filter}
              getRankList={getRankList}
              showRank={showRank}
              title={formatMessage({ id: item.text })}
              logId="KMPR_CompetitorRanking"
              cardClickCallback={() => {
                goPage({
                  path: `/opinion/Feeds/Feeds?tab=newMedia`,
                  filter: { ...filter, ...(item.otherParams || {}) },
                  // Feeds 为特殊页面，走本地缓存
                  mode: EDrilldownMode.Local,
                });
              }}
              smallChart={true}
              smallChartStatistics={statisticsMap[item.key]}
            ></KeyMetricsItem>
          ))}
        </>
      </CardCommon>
    </div>
  );
}
